/**
 * <photo-marquee> - continuous auto-scrolling row of photos. Clones its
 * items until the track is wide enough to loop seamlessly, then scrolls it
 * with a compositor-driven animation (Web Animations API, no per-frame JS).
 * Only set up once visible, paused when off-screen or hovered, and respects
 * prefers-reduced-motion.
 */
class PhotoMarquee extends HTMLElement {
  /** px per second (matches the former 0.6px per frame at 60fps) */
  static SPEED = 36;

  connectedCallback() {
    this.track = this.querySelector('[data-marquee-track]');
    if (!this.track || !this.track.children.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible && !this.animation) this.#setup();
        if (this.visible) this.#resume();
        else this.#pause();
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.animation?.cancel();
    this.animation = null;
  }

  #setup() {
    const items = Array.from(this.track.children);
    const wrapperWidth = this.getBoundingClientRect().width;
    if (!wrapperWidth) return;

    let sets = 1;
    while (this.track.scrollWidth < wrapperWidth * 3 && sets < 11) {
      items.forEach((item) => this.track.appendChild(item.cloneNode(true)));
      sets += 1;
    }

    // Width of one full set of items, trailing gap included: shifting by
    // exactly that distance lands on an identical frame.
    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const setWidth = (this.track.scrollWidth + gap) / sets;
    if (!setWidth) return;

    this.animation = this.track.animate(
      [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${-setWidth}px, 0, 0)` }],
      { duration: (setWidth / PhotoMarquee.SPEED) * 1000, iterations: Infinity, easing: 'linear' }
    );
  }

  #pause = () => this.animation?.pause();

  #resume = () => {
    if (this.visible && !this.matches(':hover')) this.animation?.play();
  };
}

if (!customElements.get('photo-marquee')) {
  customElements.define('photo-marquee', PhotoMarquee);
}
